/**
 * HostRateGate — compuerta de ritmo compartida, indexada por *host*
 *
 * FASE-A (`OBS-GEO-NOMINATIM-DOBLE-CAMINO-SIN-COMPUERTA`): el freno pertenece
 * al servidor, no a la clase. Todo llamante a un host con límite publicado
 * pasa por aquí, venga de la clase que venga.
 *
 * Valores heredados sin cambios de BUG-01-S1 (1100 ms para Nominatim).
 * Los números los calibrará DOSSIER §4 cuando despierte; esta pieza solo
 * decide DÓNDE vive el freno, no cuánto frena.
 *
 * NOMFIX-a1a (`OBS-GEO-NOMINATIM-429-NO-LEE-RETRY-AFTER-Y-LA-RUTA-TELECOM-NO-CONSULTA-EL-CORTACIRCUITOS`,
 * DOP-3 (a) de NOMPOL): cuando un host contesta 429, quien lo vea llama a
 * `reportRateLimited`; el host queda en enfriamiento y TODO llamante que pase
 * por `acquireHostSlot` (orquestador, Overpass/telecom, sondeos) espera, sin
 * inyectar el cortacircuitos del orquestador en cada cliente.
 *
 * Punto de extensión futuro (no implementado a propósito): prioridades de
 * cola para relleno en segundo plano (`OBS-GEO-COLA-FONDO-PRIORIDADES`).
 *
 * @module services/geocoding
 */

import { parseRetryAfter } from '../../utils/fetchWithBackoff';

/** Tope del enfriamiento tras un 429 (el mismo que `fetchWithBackoff`). */
const MAX_COOLDOWN_MS = 30_000;

/** Intervalos mínimos por host (ms). Hosts no listados: paso libre. */
const HOST_MIN_INTERVAL_MS: Record<string, number> = {
  // Política publicada Nominatim: 1 req/s. Margen BUG-01-S1: 1100 ms.
  'nominatim.openstreetmap.org': 1100,
};

const _lastRequest = new Map<string, number>();
const _tail = new Map<string, Promise<void>>();
/** Instante (ms epoch) hasta el que un host está en enfriamiento tras un 429. */
const _blockedUntil = new Map<string, number>();

/** Extrae el host de una URL completa; si ya es un host, lo devuelve tal cual. */
function hostOf(hostOrUrl: string): string {
  if (!hostOrUrl.includes('/')) return hostOrUrl;
  try {
    return new URL(hostOrUrl).host;
  } catch {
    return hostOrUrl;
  }
}

/**
 * Espera el turno para el host indicado.
 *
 * Serializa a TODOS los llamantes concurrentes mediante cadena de promesas
 * por host: N llamantes simultáneos ⇒ N despachos separados ≥ intervalo,
 * no un estampido colectivo tras la primera espera (defecto del patrón
 * «leer-esperar-escribir» de la compuerta original BUG-01-S1 con N≥3).
 *
 * @param hostOrUrl Host (`nominatim.openstreetmap.org`) o URL completa.
 */
export async function acquireHostSlot(hostOrUrl: string): Promise<void> {
  const host = hostOf(hostOrUrl);
  const interval = HOST_MIN_INTERVAL_MS[host];
  if (!interval) return; // host sin límite registrado: paso libre

  const prev = _tail.get(host) ?? Promise.resolve();
  const current = prev.then(async () => {
    // Se vuelve a comprobar tras cada espera: un 429 que llega mientras este
    // turno duerme (la respuesta de la petición anterior) debe alargarla
    // (medido en NOMFIX: con una sola comprobación el enfriamiento no frenaba
    // a quienes ya estaban en cola).
    for (;;) {
      const ahora = Date.now();
      const espera = Math.max(
        (_blockedUntil.get(host) ?? 0) - ahora,
        interval - (ahora - (_lastRequest.get(host) ?? 0)),
      );
      if (espera <= 0) break;
      await new Promise<void>((r) => setTimeout(r, espera));
    }
    _lastRequest.set(host, Date.now());
  });
  _tail.set(host, current);
  await current;
}

/**
 * Anota que el host contestó 429: nadie le pedirá nada hasta que pase el
 * enfriamiento. Dura lo que diga `Retry-After` (segundos o fecha HTTP), con un
 * mínimo de dos intervalos del host (el primer retroceso del cliente de
 * Nominatim: 2,2 s) y un tope de 30 s. Un aviso más corto no acorta uno vigente.
 *
 * @param hostOrUrl Host o URL completa.
 * @param retryAfterHeader Valor de la cabecera `Retry-After`, si vino.
 * @returns Milisegundos de enfriamiento aplicados (0 si el host no tiene límite registrado).
 */
export function reportRateLimited(hostOrUrl: string, retryAfterHeader?: string | null): number {
  const host = hostOf(hostOrUrl);
  const interval = HOST_MIN_INTERVAL_MS[host];
  if (!interval) return 0;
  const pedido = parseRetryAfter(retryAfterHeader ?? null);
  const espera = Math.min(MAX_COOLDOWN_MS, Math.max(pedido, interval * 2));
  const hasta = Date.now() + espera;
  _blockedUntil.set(host, Math.max(_blockedUntil.get(host) ?? 0, hasta));
  return espera;
}

/**
 * Lee `Retry-After` de un error de axios (o de cualquier objeto con
 * `response.headers`): acepta `AxiosHeaders` (`get`) y objetos planos.
 * Devuelve `null` si no hay respuesta, no hay cabecera o está vacía.
 */
export function retryAfterHeaderOf(error: unknown): string | null {
  const headers = (error as {
    response?: { headers?: { get?: (nombre: string) => unknown } & Record<string, unknown> };
  } | null | undefined)?.response?.headers;
  if (!headers) return null;
  const valor = typeof headers.get === 'function'
    ? headers.get('retry-after')
    : (headers['retry-after'] ?? headers['Retry-After']);
  return valor === undefined || valor === null || valor === '' ? null : String(valor);
}

/** Intervalo mínimo registrado para un host, en ms (0 si no tiene límite). */
export function getHostMinIntervalMs(hostOrUrl: string): number {
  return HOST_MIN_INTERVAL_MS[hostOf(hostOrUrl)] ?? 0;
}

/** Solo para tests: resetea el estado de todos los hosts. */
export function _resetHostRateGateForTests(): void {
  _lastRequest.clear();
  _tail.clear();
  _blockedUntil.clear();
}
